import { MessageCircle, Monitor } from "lucide-react";
import { Button, RouteLink } from "../../components/ui";

const StepHeading = ({ step, title, dark = false }: { step: number; title: string; dark?: boolean }) => (
  <h2 className="flex items-center gap-4">
    <span
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[15px] font-semibold ${
        dark ? "bg-primary text-white" : "bg-primary/10 text-primary"
      }`}
    >
      {step}
    </span>
    <span className={`text-[22px] font-bold tracking-tight sm:text-[24px] ${dark ? "text-white" : "text-body-text"}`}>
      {title}
    </span>
  </h2>
);

const StepLabel = ({ children, dark = false }: { children: React.ReactNode; dark?: boolean }) => (
  <p
    className={`mb-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] ${
      dark ? "text-white/50" : "text-body-text/45"
    }`}
  >
    {children}
  </p>
);

export const HowItWorks = () => {
  return (
    <article>
      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="relative min-h-[55vh] overflow-hidden bg-black">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('/image.png')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/90 via-[#0a1628]/75 to-[#0a1628]/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628]/70 via-transparent to-[#0a1628]/40" />
        <div className="relative z-10 flex min-h-[55vh] items-center px-6 pb-16 pt-32 sm:px-10 lg:px-20">
          <div className="max-w-2xl space-y-4">
            <h1 className="animate-fade-up text-[28px] font-semibold leading-[1.12] tracking-tight text-white sm:text-[36px] lg:text-[40px]">
              How it works
            </h1>
            <p className="animate-fade-up text-pretty text-[16px] font-normal leading-relaxed text-white/80 [animation-delay:100ms] sm:text-[17px]">
              From first question to funding match in four steps
            </p>
          </div>
        </div>
      </section>

      {/* ── Step 1 — white bg ─────────────────────────────────── */}
      <section className="bg-white px-6 py-20 sm:px-10 lg:px-20">
        <div className="grid items-start gap-12 lg:grid-cols-[3fr_2fr]">
            <div className="space-y-6">
              <StepHeading step={1} title="Create Your Profile" />
              <div className="space-y-5">
                <div>
                  <StepLabel>What happens</StepLabel>
                  <p className="text-pretty text-[16px] font-normal leading-relaxed text-body-text/80 sm:text-[17px]">
                    Answer structured questions about your business through our web interface or
                    WhatsApp. We&apos;ll ask about your company stage, sector, business model, team,
                    traction, and financial needs.
                  </p>
                </div>
                <div>
                  <StepLabel>Time required</StepLabel>
                  <p className="text-[16px] font-normal text-body-text/80 sm:text-[17px]">About 10 minutes</p>
                </div>
                <div>
                  <StepLabel>What you&apos;ll need</StepLabel>
                  <p className="text-pretty text-[16px] font-normal leading-relaxed text-body-text/80 sm:text-[17px]">
                    Basic information about your business—no documents required to start.
                  </p>
                </div>
              </div>
            </div>
            <img
              src="/how-it-works.png"
              alt="How Fundy works"
              className="w-full rounded-2xl object-cover shadow-xl shadow-black/10 ring-1 ring-black/10"
            />
        </div>
      </section>

      {/* ── Step 2 — dark bg ──────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('/image.png')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628]/95 via-[#0a1628]/85 to-[#0a1628]/95" />
        <div className="relative z-10 px-6 py-20 sm:px-10 lg:px-20">
          <div className="grid items-start gap-12 lg:grid-cols-[3fr_2fr]">
              <div className="space-y-6">
                <StepHeading step={2} title="Get Your Investment Readiness Score" dark />
                <div className="space-y-5">
                  <div>
                    <StepLabel dark>What happens</StepLabel>
                    <p className="mb-3 text-pretty text-[16px] font-normal leading-relaxed text-white/80 sm:text-[17px]">
                      Fundy analyzes your profile against the criteria investors actually use to
                      evaluate opportunities. You receive a score across key dimensions:
                    </p>
                    <ul className="list-none space-y-2">
                      {[
                        "Team strength and experience",
                        "Market opportunity and positioning",
                        "Traction and validation",
                        "Financial clarity and projections",
                        "Documentation readiness",
                      ].map((item) => (
                        <li key={item} className="flex items-start gap-3 text-[16px] font-normal text-white/85 sm:text-[17px]">
                          <span className="mt-[0.55em] h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <StepLabel dark>What you get</StepLabel>
                    <p className="text-pretty text-[16px] font-normal leading-relaxed text-white/80 sm:text-[17px]">
                      A clear assessment of where you stand, specific gaps to address, and
                      prioritized actions to improve your score before approaching investors.
                    </p>
                  </div>
                </div>
              </div>
              <img
                src="/how-it-works.png"
                alt="Investment Readiness Score"
                className="w-full rounded-2xl object-cover shadow-xl shadow-black/30 ring-1 ring-white/10"
              />
          </div>
        </div>
      </section>

      {/* ── Step 3 — white bg ─────────────────────────────────── */}
      <section className="bg-white px-6 py-20 sm:px-10 lg:px-20">
        <div className="grid items-start gap-12 lg:grid-cols-[3fr_2fr]">
            <div className="space-y-6">
              <StepHeading step={3} title="Receive Funding Type Recommendations" />
              <div className="space-y-5">
                <div>
                  <StepLabel>What happens</StepLabel>
                  <p className="mb-3 text-pretty text-[16px] font-normal leading-relaxed text-body-text/80 sm:text-[17px]">
                    Based on your profile, Fundy recommends which of six funding types best fit your
                    situation:
                  </p>
                  <ul className="list-none space-y-2">
                    {[
                      ["Grants", "Non-dilutive funding for specific purposes"],
                      ["Seed Equity", "Early investment for ownership stake"],
                      ["Venture Capital", "Growth capital from institutional investors"],
                      ["Debt", "Loans and credit facilities"],
                      ["Revenue-based Finance", "Funding tied to your revenue"],
                      ["Blended Finance", "Combinations of the above"],
                    ].map(([name, blurb]) => (
                      <li key={name} className="flex items-start gap-3 text-[16px] font-normal text-body-text/80 sm:text-[17px]">
                        <span className="mt-[0.55em] h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                        <span>
                          <span className="font-semibold text-body-text">{name}:</span> {blurb}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <StepLabel>What you get</StepLabel>
                  <p className="text-pretty text-[16px] font-normal leading-relaxed text-body-text/80 sm:text-[17px]">
                    Personalised recommendations with plain-language rationales explaining why each
                    option fits (or doesn&apos;t fit) your business.
                  </p>
                </div>
              </div>
            </div>
            {/* Circular photo */}
            <div className="flex justify-center pt-4 lg:justify-end">
              <div className="relative">
                <div aria-hidden className="absolute -inset-5 rounded-full bg-primary/10 blur-2xl" />
                <div className="relative h-72 w-72 overflow-hidden rounded-full shadow-xl ring-1 ring-black/10 sm:h-80 sm:w-80">
                  <img src="/image.png" alt="Funding recommendation meeting" className="h-full w-full object-cover" />
                </div>
              </div>
            </div>
        </div>
      </section>

      {/* ── Step 4 — dark bg ──────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('/image.png')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628]/95 via-[#0a1628]/85 to-[#0a1628]/95" />
        <div className="relative z-10 px-6 py-20 sm:px-10 lg:px-20">
          <div className="max-w-4xl space-y-6">
            <StepHeading step={4} title="Connect with Opportunities" dark />
            <div className="space-y-5">
              <div>
                <StepLabel dark>What happens</StepLabel>
                <p className="mb-3 text-pretty text-[16px] font-normal leading-relaxed text-white/80 sm:text-[17px]">
                  Fundy connects you to matched funding opportunities and the specialized platforms
                  you&apos;ll need to execute your raise:
                </p>
                <ul className="list-none space-y-2">
                  {[
                    "Deal discovery through Kenya Dealroom and partner databases",
                    "Due diligence preparation through Deal Stack referrals",
                    "Documentation support for pitch decks, data rooms, and legal",
                    "Investor introductions based on sector and stage fit",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-[16px] font-normal text-white/85 sm:text-[17px]">
                      <span className="mt-[0.55em] h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <StepLabel dark>What you get</StepLabel>
                <p className="text-pretty text-[16px] font-normal leading-relaxed text-white/80 sm:text-[17px]">
                  A clear path forward with the right tools at each step—not just a list of
                  investors, but guidance through the actual process of raising capital.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Access Options — white bg ─────────────────────────── */}
      <section className="bg-white px-6 py-20 sm:px-10 lg:px-20">
        <div className="space-y-8">
          <h2 className="text-[22px] font-bold tracking-tight text-body-text sm:text-[24px]">Access Options</h2>
          <div className="space-y-6">
            <div className="flex gap-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Monitor className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <p className="mb-1 pt-1.5 text-[16px] font-semibold text-body-text sm:text-[17px]">Web Platform</p>
                <p className="max-w-3xl text-pretty text-[16px] font-normal leading-relaxed text-body-text/80 sm:text-[17px]">
                  Full-featured interface with visual dashboards, detailed scoring breakdowns, and
                  document management.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <MessageCircle className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <p className="mb-1 pt-1.5 text-[16px] font-semibold text-body-text sm:text-[17px]">WhatsApp</p>
                <p className="max-w-3xl text-pretty text-[16px] font-normal leading-relaxed text-body-text/80 sm:text-[17px]">
                  Chat directly with Fundy for guidance, quick questions, and recommendations. Tag
                  @fundy in group chats to get insights on shared content.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA — dark photo bg ───────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('/image.png')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628]/90 via-[#0a1628]/80 to-[#0a1628]/90" />
        <div className="relative z-10 px-6 py-20 sm:px-10 lg:px-20">
          <div className="space-y-7">
            <h2 className="max-w-3xl text-pretty text-[22px] font-bold leading-snug tracking-tight text-white sm:text-[24px]">
              Get your Investment Readiness Score and funding recommendations.
            </h2>
            <div className="flex flex-wrap gap-3">
              <RouteLink to="/auth/signup">
                <Button
                  size="lg"
                  className="h-11 rounded-full bg-primary px-8 text-sm font-semibold text-white shadow-lg shadow-primary/40 transition-all duration-200 ease-out hover:bg-primary/90 hover:shadow-xl hover:shadow-primary/40"
                >
                  Create Your Profile
                </Button>
              </RouteLink>
              <a href="https://wa.me/254700000000" target="_blank" rel="noopener noreferrer">
                <Button
                  size="lg"
                  variant="outline"
                  className="h-11 rounded-full border-white/30 bg-white/10 px-8 text-sm font-semibold text-white backdrop-blur-md transition-all duration-200 ease-out hover:border-white/60 hover:bg-white/20 hover:text-white"
                >
                  <MessageCircle className="mr-2 h-4 w-4" />
                  Chat on WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </article>
  );
};
